import React, { createContext, useContext, useState } from "react";
import Notification from "@components/notification";

// Define the shape of a notification
type NotificationType = {
  id: number;
  message: string;
};

// Define the context type
type NotificationContextType = {
  addNotification: (message: string) => void;
};

// Create the context
const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

// Custom hook to access the notification context
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  }
  return context;
};

// NotificationProvider component
export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<NotificationType[]>([]);

  // Function to add a new notification
  const addNotification = (message: string) => {
    const id = Date.now(); // Use timestamp as a unique ID
    setNotifications((prev) => [...prev, { id, message }]);
  };

  // Function to remove a notification by ID
  const removeNotification = (id: number) => {
    setNotifications((prev) => prev.filter((notif) => notif.id !== id));
  };

  return (
    <NotificationContext.Provider value={{ addNotification }}>
      {children}
      <div style={{ position: "fixed", top: 0, right: 0, zIndex: 1000 }}>
        {notifications.map((notif) => (
          <Notification
            key={notif.id}
            id={notif.id}
            message={notif.message}
            remove={removeNotification}
          />
        ))}
      </div>
    </NotificationContext.Provider>
  );
};
